import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useBdrPanel } from '@/hooks/useBdrPanel'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { BdrPulseDashboard, BdrQueue, BdrAllLeadsTable, BdrNurturingTable, BdrHistoryTable, BdrBankTable, BdrMetricsComparison, BdrDistributionHistory, BdrPerformanceReport, BdrImportCsv, BdrAutoProspection } from '@/components/BdrPanelV2'
import { BdrLeadManagerModal, BdrHelpModal } from '@/components/BdrPanelV2/Modals'
import { LayoutGrid, List, HeartPulse, History, Plus, HelpCircle, Eye, EyeOff, BarChart3, GitBranch, TrendingUp } from 'lucide-react'
import {
  PageToolbar,
  SegmentTabs,
  ToolbarControlsGroup,
  ToolbarIconButton,
} from '@/components/ui/page-toolbar'

export default function BdrPanel() {
  const hook = useBdrPanel()
  const { viewTab, setViewTab } = hook



  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isMetricsVisible, setIsMetricsVisible] = useState(() => {
    const saved = localStorage.getItem('bdr_metrics_visible')
    return saved !== null ? JSON.parse(saved) : true
  })

  useEffect(() => {
    localStorage.setItem('bdr_metrics_visible', JSON.stringify(isMetricsVisible))
  }, [isMetricsVisible])

  const tabs = [
    { id: 'queue', label: 'Minha Fila', icon: LayoutGrid },
    { id: 'all', label: 'Todos os Leads', icon: List },
    { id: 'nurturing', label: 'Nutrição', icon: HeartPulse },
    { id: 'history', label: 'Histórico', icon: History },
    { id: 'metrics', label: 'Métricas', icon: BarChart3 },
    { id: 'distribution', label: 'Distribuições', icon: GitBranch },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
  ]

  return (
    <DashboardLayout title="Leads & BDR">
      <div className="space-y-6">
        {/* Pulse Dashboard */}
        <AnimatePresence>
          {isMetricsVisible && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <BdrPulseDashboard
                stats={hook.stats}
                planningCount={hook.queueGroups.planejamento.length}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <PageToolbar
          navigation={
            <SegmentTabs
              tabs={tabs.map((t) => ({ id: t.id, label: t.label, icon: t.icon }))}
              value={viewTab}
              onChange={setViewTab}
              ariaLabel='Abas do painel BDR'
            />
          }
          controls={
            <ToolbarControlsGroup>
              <span
                className='flex h-10 min-w-[10rem] items-center rounded-lg border border-border bg-surface px-3 text-xs font-semibold text-foreground'
                title='Carteira BDR'
              >
                {hook.operationalBdrName}
              </span>
              <ToolbarIconButton
                onClick={() => setIsHelpOpen(true)}
                title='Guia Estratégico BDR'
                aria-label='Guia Estratégico BDR'
              >
                <HelpCircle size={16} />
              </ToolbarIconButton>
              <ToolbarIconButton
                active={isMetricsVisible}
                onClick={() => setIsMetricsVisible(!isMetricsVisible)}
                title={isMetricsVisible ? 'Ocultar Métricas' : 'Mostrar Métricas'}
                aria-label={isMetricsVisible ? 'Ocultar Métricas' : 'Mostrar Métricas'}
              >
                {isMetricsVisible ? <Eye size={16} /> : <EyeOff size={16} />}
              </ToolbarIconButton>
              <BdrImportCsv />
              <button
                type='button'
                onClick={hook.handleOpenNewLead}
                className='flex h-10 items-center gap-1.5 rounded-lg bg-accent px-3 text-xs font-bold text-white transition-all hover:opacity-90'
            >
              <Plus size={13} />
              Novo Lead
            </button>
            </ToolbarControlsGroup>
          }
        />

        {/* Content */}
        {viewTab === 'queue' && (
          <BdrQueue
            queueGroups={hook.queueGroups}
            collapsed={hook.collapsed}
            toggleGroup={hook.toggleGroup}
            openLeadDetail={hook.handleOpenLeadDetail}
          />
        )}
        {viewTab === 'all' && (
          <BdrAllLeadsTable
            filteredAllLeads={hook.filteredAllLeads}
            filters={hook.filtersAll}
            openLeadDetail={hook.handleOpenLeadDetail}
            openRegTentativaModal={(lead) => {
              hook.handleOpenLeadDetail(lead, true)
            }}
            loading={hook.loading}
            isFiltering={hook.isFiltering}
            loadingMore={hook.loadingMore}
            hasMore={hook.hasMore}
            totalLeads={hook.totalLeads}
            handleLoadMore={hook.handleLoadMore}
          />
        )}
        {viewTab === 'bank' && (
          <BdrBankTable
            onSelectLead={(lead) => hook.handleOpenLeadDetail(lead)}
          />
        )}
        {viewTab === 'nurturing' && (
          <BdrNurturingTable
            nurturingLeads={hook.nurturingLeads}
            loadingNurturing={hook.loadingNurturing}
            filters={hook.filtersNurturing}
            fetchNurturingLeads={hook.fetchNurturingLeads}
            handleRecoverLead={hook.handleRecoverLead}
            handleDiscardLead={hook.handleDiscardLead}
            openLeadDetail={hook.handleOpenLeadDetail}
          />
        )}
        {viewTab === 'history' && (
          <BdrHistoryTable
            historyData={hook.historyAgendamentos}
            loading={hook.loadingHistory}
            openLeadDetail={hook.handleOpenLeadDetail}
          />
        )}
        {viewTab === 'metrics' && (
          <BdrMetricsComparison />
        )}
        {viewTab === 'distribution' && (
          <BdrDistributionHistory bdrId={hook.currentBdrId} />
        )}
        {viewTab === 'performance' && (
          <div className="space-y-6">
            <BdrPerformanceReport bdrId={hook.currentBdrId} />
            <BdrAutoProspection bdrId={hook.currentBdrId} />
          </div>
        )}
      </div>

      <BdrLeadManagerModal hook={hook} />
      <BdrHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </DashboardLayout>
  )
}
